/**
 * BDL-1609 client half: make a stale dev bundle LOUD in the browser, which
 * is where the harm actually lands -- someone verifying a frontend change at
 * :3000 against code the server never rebuilt, and getting a confidently
 * wrong answer in either direction.
 *
 * Mirrors installTailwindFreshnessDevHelper.ts (BDL-2808), which does the
 * same job for stale Tailwind CSS: a `window`-callable check plus a loud
 * console verdict, so an agent-browser/Playwright sweep has something
 * mechanized to read instead of eyeballing the page.
 */
import AppConfig from 'shared/config-public';

import {
	DEV_FRESHNESS_ENDPOINT,
	DevBundleFreshnessResult,
} from './devBundleFreshness';

/** How often to re-check. A stale bundle does not heal on its own, so this
 * is about catching a server that goes stale WHILE a tab is open. */
const POLL_INTERVAL_MS = 60_000;

type ProbeResponse = DevBundleFreshnessResult & {
	newestSourcePath?: string | null;
};

/** Last verdict reported, so a healthy server polling every minute does not
 * spam the console with an identical "fresh" line forever. A CHANGE is
 * news; a steady state is not. */
let lastReportedVerdict: string | null = null;

/** Distinct from the three verdicts, so "the route is missing" dedups on its
 * own terms and a later real verdict still counts as a change. */
const NO_PROBE = 'no-probe';

export async function checkDevBundleFreshness(): Promise<ProbeResponse | null> {
	let response: Response;
	try {
		response = await fetch(DEV_FRESHNESS_ENDPOINT, {
			cache: 'no-store',
		});
	} catch (err) {
		return null;
	}

	// An HTTP 200 is NOT enough: rsbuild's SPA fallback answers unknown
	// paths with index.html and a 200, so a missing route would otherwise
	// parse as "everything is fine". Require real JSON.
	const contentType = response.headers.get('content-type') || '';
	if (!response.ok || !contentType.includes('application/json')) {
		return null;
	}

	try {
		return (await response.json()) as ProbeResponse;
	} catch (err) {
		return null;
	}
}

function report(result: ProbeResponse): void {
	const verdictChanged = result.verdict !== lastReportedVerdict;
	lastReportedVerdict = result.verdict;

	// Every branch below obeys the one rule this module states above: a
	// CHANGE is news, a steady state is not. Round 1 exempted `stale` from
	// the dedup so a real problem would keep shouting; with the false-STALE
	// class closed that exemption is no longer worth its cost, because
	// `console.error` every 60s forever is itself a train-people-to-ignore-it
	// failure. The message persists in the console once logged.
	if (!verdictChanged) return;

	if (result.verdict === 'stale') {
		// eslint-disable-next-line no-console
		console.error(
			`[bundle-freshness] STALE BUNDLE -- what you are looking at is NOT the code on disk. ${result.reason} Restart the dev server (npm start) before trusting anything you see here.`,
		);
		return;
	}

	if (result.verdict === 'unknown') {
		// eslint-disable-next-line no-console
		console.warn(
			`[bundle-freshness] INCONCLUSIVE (not a pass): ${result.reason}`,
		);
		return;
	}

	if (result.autoReloadDisabled) {
		// eslint-disable-next-line no-console
		console.warn(
			`[bundle-freshness] fresh right now, but DISABLE_AUTO_RELOAD is set on this server -- it will NOT pick up the next edit. ${result.reason}`,
		);
		return;
	}

	// eslint-disable-next-line no-console
	console.info(`[bundle-freshness] fresh: ${result.reason}`);
}

/**
 * Fetch once, report loudly, then poll. Returns the result so a caller (or
 * an agent via `window.assertBundleFresh()`) can assert on it directly.
 */
export async function assertBundleFresh(): Promise<ProbeResponse | null> {
	const result = await checkDevBundleFreshness();

	if (!result) {
		// Same dedup rule: without it, a build with no probe route (an older
		// bundle, or a non-rsbuild server) warns every POLL_INTERVAL_MS
		// forever about a condition that will never change.
		if (lastReportedVerdict !== NO_PROBE) {
			lastReportedVerdict = NO_PROBE;
			// eslint-disable-next-line no-console
			console.warn(
				`[bundle-freshness] INCONCLUSIVE (not a pass): no probe at ${DEV_FRESHNESS_ENDPOINT}. This build predates BDL-1609, or you are not on an rsbuild dev server.`,
			);
		}
		return null;
	}

	report(result);
	return result;
}

/**
 * Dev/worktree only -- a real user's session has no dev server to ask, and
 * this must never cost a prod bundle anything.
 */
export function installDevBundleFreshnessCheck(): void {
	if (AppConfig.buildEnv === 'prod') return;
	if (typeof window === 'undefined') return;

	(
		window as unknown as {
			assertBundleFresh: typeof assertBundleFresh;
		}
	).assertBundleFresh = assertBundleFresh;

	// Fire-and-forget: a freshness probe must never block or break boot.
	assertBundleFresh().catch(() => {});
	setInterval(() => {
		assertBundleFresh().catch(() => {});
	}, POLL_INTERVAL_MS);
}
